import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Send, Trash2, ArrowLeft, MoreVertical, Flag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { socialAPI, BACKEND_URL } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import SimpleVideoPlayer from '../components/SimpleVideoPlayer';

const PostDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { toast } = useToast();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadPost();
    }, [id]);

    const loadPost = async () => {
        try {
            setLoading(true);
            const response = await socialAPI.getPost(id);
            setPost(response.data);
        } catch (error) {
            toast({ title: t('common.error'), description: t('feed.postNotFound'), variant: 'destructive' });
            navigate('/feed'); // Redirect to feed if not found
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async () => {
        if (!post) return;
        try {
            const response = await socialAPI.toggleLike(post.id);
            setPost(prev => ({
                ...prev,
                is_liked: response.data.liked,
                like_count: response.data.like_count
            }));
        } catch (error) {
            toast({ title: t('common.error'), variant: 'destructive' });
        }
    };

    const handleComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || submitting || !post) return;

        try {
            setSubmitting(true);
            const response = await socialAPI.addComment(post.id, newComment);
            setPost(prev => ({
                ...prev,
                comment_count: prev.comment_count + 1,
                comments: [...(prev.comments || []), response.data]
            }));
            setNewComment('');
        } catch (error) {
            toast({ title: t('common.error'), variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await socialAPI.deleteComment(commentId);
            setPost(prev => ({
                ...prev,
                comment_count: prev.comment_count - 1,
                comments: prev.comments.filter(c => c.id !== commentId)
            }));
        } catch (error) {
            toast({ title: t('common.error'), variant: 'destructive' });
        }
    };

    const [reportDialog, setReportDialog] = useState({ open: false, commentId: null, reason: '' });
    const [activeMenu, setActiveMenu] = useState(null);

    const openReportDialog = (commentId) => {
        setReportDialog({ open: true, commentId, reason: '' });
        setActiveMenu(null);
    };

    const submitReport = async () => {
        if (!reportDialog.commentId) return;
        try {
            await socialAPI.reportComment(reportDialog.commentId, reportDialog.reason);
            toast({ title: t('common.success'), description: t('feed.commentReported') });
            setReportDialog({ open: false, commentId: null, reason: '' });
        } catch (error) {
            toast({ title: t('common.error'), description: error.response?.data?.detail || t('feed.reportError'), variant: 'destructive' });
        }
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-400">{t('common.loading')}</div>;
    }

    if (!post) return null;

    const isTeamPost = !!post.team_id;
    const authorName = isTeamPost ? post.team?.name : post.author?.username;
    const authorAvatar = isTeamPost ? post.team?.logo_url : post.author?.avatar_url;
    const authorLink = isTeamPost ? `/teams/${post.team_id}/profile` : `/profile/${post.author?.username}`;

    return (
        <div className="p-6 max-w-2xl mx-auto pt-16 lg:pt-6">
            <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mb-4 text-gray-400 hover:text-white pl-0"
            >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.back')}
            </Button>

            <Card className="bg-[#1a1a1b] border-gray-800 mb-4">
                <CardContent className="p-4">
                    {/* Author Header */}
                    <div className="flex items-center gap-3 mb-3">
                        <Link to={authorLink}>
                            <Avatar className="h-10 w-10">
                                <AvatarImage src={authorAvatar} />
                                <AvatarFallback className={isTeamPost ? "bg-blue-900" : "bg-blue-600"}>
                                    {authorName?.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                        </Link>
                        <div>
                            <Link to={authorLink} className="text-sm font-semibold text-white hover:underline">
                                {authorName}
                            </Link>
                            <p className="text-xs text-gray-500">
                                {post.created_at ? format(parseISO(post.created_at), 'MMM d, yyyy') : ''}
                            </p>
                        </div>
                    </div>

                    {/* Video */}
                    {post.video && (
                        <div className="relative mb-3 bg-black rounded-lg overflow-hidden flex justify-center">
                            <SimpleVideoPlayer
                                src={(() => {
                                    const rawUrl = post.video.video_url || post.video.url;
                                    const urlWithToken = `${rawUrl}${rawUrl?.includes('/stream') ? `?token=${localStorage.getItem('token')}` : ''}`;
                                    if (urlWithToken.startsWith('http')) return urlWithToken;
                                    return `${BACKEND_URL}${urlWithToken}`;
                                })()}
                                className="w-full max-h-[80vh] object-contain"
                                poster={post.video.thumbnail_url?.startsWith('http') ? post.video.thumbnail_url : (post.video.thumbnail_url ? `${BACKEND_URL}${post.video.thumbnail_url}` : undefined)}
                            />
                        </div>
                    )}

                    {/* Caption */}
                    {post.caption && (
                        <p className="text-white mb-3 text-lg">{post.caption}</p>
                    )}

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {post.tags.map((tag, i) => (
                                <span key={i} className="text-blue-400 text-sm">#{tag}</span>
                            ))}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-700">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLike}
                            className={`${post.is_liked ? 'text-red-500' : 'text-gray-400'} hover:text-red-500`}
                        >
                            <Heart className={`h-5 w-5 mr-1 ${post.is_liked ? 'fill-current' : ''}`} />
                            {post.like_count}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-400"
                        >
                            <MessageCircle className="h-5 w-5 mr-1" />
                            {post.comment_count}
                        </Button>
                    </div>

                    {/* Comments Section - Always Visible on Detail Page */}
                    <div className="mt-4 pt-4 border-t border-gray-700">
                        {post.comments && post.comments.map((comment) => (
                            <div key={comment.id} className="flex items-start gap-2 mb-3 group relative">
                                <Link to={`/profile/${comment.username}`} className="text-blue-400 text-sm font-medium hover:underline">
                                    {comment.username}
                                </Link>
                                <p className="text-gray-300 text-sm flex-1">{comment.text}</p>

                                {((comment.username === user?.username) || user?.role === 'admin') ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="text-gray-500 hover:text-red-500 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                ) : (
                                    <div className="relative">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setActiveMenu(activeMenu === comment.id ? null : comment.id)}
                                            className="text-gray-500 hover:text-white h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <MoreVertical className="h-3 w-3" />
                                        </Button>
                                        {activeMenu === comment.id && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
                                                <div className="absolute right-0 top-6 z-20 bg-[#2a2a2b] border border-gray-700 rounded shadow-xl py-1 w-32">
                                                    <button
                                                        onClick={() => openReportDialog(comment.id)}
                                                        className="w-full text-left px-3 py-1.5 text-xs text-orange-400 hover:bg-white/5 flex items-center gap-2"
                                                    >
                                                        <Flag className="h-3 w-3" />
                                                        {t('common.report')}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        <form onSubmit={handleComment} className="flex gap-2 mt-3">
                            <Input
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder={t('feed.addComment')}
                                className="bg-[#2a2a2b] border-gray-700 text-white flex-1"
                                maxLength={300}
                            />
                            <Button type="submit" size="sm" disabled={!newComment.trim() || submitting}>
                                <Send className="h-4 w-4" />
                            </Button>
                        </form>
                    </div>
                </CardContent>
            </Card>

            {/* Report Dialog */}
            <Dialog open={reportDialog.open} onOpenChange={(open) => setReportDialog(prev => ({ ...prev, open }))}>
                <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
                    <DialogHeader>
                        <DialogTitle>{t('feed.reportCommentTitle')}</DialogTitle>
                        <DialogDescription className="text-gray-400">
                            {t('feed.reportReasonDesc')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            value={reportDialog.reason}
                            onChange={(e) => setReportDialog(prev => ({ ...prev, reason: e.target.value }))}
                            placeholder={t('feed.reportReasonPlaceholder')}
                            className="bg-[#2a2a2b] border-gray-700 text-white"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReportDialog(prev => ({ ...prev, open: false }))} className="text-gray-400">
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={submitReport} className="bg-orange-600 hover:bg-orange-700 text-white">
                            {t('common.report')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PostDetail;
